import {Footer} from '@common/ui/footer/footer';
import {MainNavbar} from '@app/main-navbar';
import {ReactNode, useEffect, useLayoutEffect, useRef} from 'react';
import {useScrollToTop} from '@common/ui/navigation/use-scroll-to-top';
import {AdHost} from '@common/admin/ads/ad-host';
import {useThemeSelector} from '@common/ui/themes/theme-selector-context';
import {closeDialog, openDialog} from '@common/ui/overlays/store/dialog-store';
import {useLocation} from 'react-router-dom';
import DownloadGuid from './downloadGuid';
// import {useSelectedLocale} from '@common/i18n/selected-locale';
//@ts-expect-error
import useGeoLocation from 'react-ipgeolocation';
import {useAuth} from '@common/auth/use-auth';
import BanDialog from './BanDialog';
import {useSelectedLocale} from '@common/i18n/selected-locale';
import {useChangeLocale} from '@common/i18n/change-locale';
import AntiAdBlock from './anti-ad-block';
import AddProfileDialog from './addProfileDialog';
import {useGetProfiles} from './profile/requests/use-get-profiles';
import SelectProfileDialog from './selectProfileDialog';
import NotLoginGuide from './notLoginGuide';
import {useUserProfile} from './profile/requests/use-user-profile';
import ZeroScoreDialog from './ZeroScoreDialog';

const PERSIAN_COUNTRIES = [
  'IR',
  'AF',
  'AM',
  'TJ',
  'AZ',
  'IN',
  'AE',
  'IQ',
  'SA',
];

interface Props {
  children: ReactNode;
}
export function SitePageLayout({children}: Props) {
  useScrollToTop();
  const {selectedTheme} = useThemeSelector();
  const isCheckingRef = useRef(false);

  const changeLocale = useChangeLocale();

  const {user, isLoggedIn} = useAuth();
  const userQuery = useUserProfile(user?.id?.toString());
  const profile = userQuery.data?.user;

  const {localeCode} = useSelectedLocale();
  // console.log(locale);
  // console.log(localeCode);

  const location = useLocation();
  const locationC = useGeoLocation();
  const getProfiles = useGetProfiles();

  //! OLD SCRIPT
  // useLayoutEffect(() => {
  //   const googleAdUrl =
  //     'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
  //     if (!location.pathname.includes("admin")) {
  //       fetch(new Request(googleAdUrl))
  //         .then(response => {
  //           if (!response.ok) {
  //             throw new Error(`HTTP error! Status: ${response.status}`);
  //           }
  //           return response.text();
  //         })
  //         .catch(err => {
  //           console.error('Fetch Error:', err);
  //           if (err.message.includes('Failed to fetch')) {
  //             console.log('Request was blocked or failed');
  //             openDialog(AntiAdBlock);
  //           } else if (err.message.includes('CORS')) {
  //             console.log('Request blocked by CORS policy');
  //           } else {
  //             console.error('Other error:', err);
  //           }
  //         });
  //     }
  // }, []);

  useLayoutEffect(() => {
    if (location.pathname.includes('admin')) return;

    const hasSeenNotLoginGuide = localStorage.getItem('hasSeenNotLoginGuide');

    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    let isHandled = false;
    const script = document.createElement('script');
    const url =
      'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';

    const timeoutId = setTimeout(() => {
      if (!isHandled) {
        isHandled = true;
        isCheckingRef.current = false;
        script.remove();
        console.log('Ad check timed out');
      }
    }, 10000);

    script.src = url;

    script.onload = () => {
      if (!isHandled) {
        isHandled = true;
        isCheckingRef.current = false;
        clearTimeout(timeoutId);
        script.remove();
        console.log('Ad script loaded');

        if (locationC.country === 'IR') {
          if (!hasSeenNotLoginGuide && !isLoggedIn) {
            openDialog(NotLoginGuide);
          }
        }
      }
    };

    script.onerror = () => {
      if (!isHandled) {
        isHandled = true;
        isCheckingRef.current = false;
        clearTimeout(timeoutId);
        script.remove();
        console.log('Ad script blocked');
        if (import.meta.env.VITE_AD_BLOCKER_STATE == 1) return;
        openDialog(AntiAdBlock);
      }
    };

    document.head.appendChild(script);

    return () => {
      clearTimeout(timeoutId);
      script.remove();
      isCheckingRef.current = false;
    };
  }, [locationC, location]);

  const hasSeenGuid = localStorage.getItem('hasSeenGuid');
  useLayoutEffect(() => {
    if (locationC.country === 'IR') {
      if (!hasSeenGuid) {
        openDialog(DownloadGuid);
      }
    }
  }, [locationC]);

  useEffect(() => {
    if (
      isLoggedIn &&
      !user?.profiles?.length &&
      !location.pathname.includes('user') &&
      user?.username !== 'admin'
    ) {
      if (getProfiles?.data?.length) {
        openDialog(SelectProfileDialog);
      } else {
        openDialog(AddProfileDialog);
      }
    } else {
      closeDialog(AddProfileDialog);
      closeDialog(SelectProfileDialog);
    }
    if (location.pathname !== '/contact') {
      if (user?.banned_at && isLoggedIn) {
        localStorage.setItem('expire_ban', user?.expire_at as string);
        openDialog(BanDialog);
      }
    } else {
      closeDialog(BanDialog);
    }
  }, [location.pathname, getProfiles]);

  useEffect(() => {
    if (localeCode !== user?.language && isLoggedIn) {
      if (localeCode === 'fa') {
        document.body.style.direction = 'rtl';
        document.documentElement.dir = 'rtl';
      } else {
        document.body.style.direction = 'ltr';
        document.documentElement.dir = 'ltr';
      }
      changeLocale.mutate({locale: localeCode});
    }
  }, [localeCode]);

  const hasSetDefault = localStorage.getItem('hasSetDefault');
  useEffect(() => {
    const countryCode = locationC.country;
    if (hasSetDefault) return;
    if (PERSIAN_COUNTRIES.includes(countryCode)) {
      changeLocale.mutate({locale: 'fa'});
      document.body.style.direction = 'rtl';
      document.documentElement.dir = 'rtl';
      localStorage.setItem('hasSetDefault', 'true');
    }
  }, [locationC]);

  useEffect(() => {
    if (profile) {
      if (profile?.score === 0) {
        localStorage.setItem('userId', profile?.id?.toString());
        openDialog(ZeroScoreDialog);
      }
    }
  }, [profile, location, user, locationC]);

  return (
    <div className="-mt-24 flex flex-col">
      <MainNavbar />
      <div
        className={`flex-auto ${selectedTheme.is_dark ? 'bg-black' : 'bg-white'}`}
      >
        {/* <AdHost slot="general_top" className="py-24" /> */}
        <div
          className={`relative z-10 min-h-[1000px] ${location.pathname.includes('titles') ? '' : 'mx-124 pt-48 [@media(max-width:767px)]:mx-12 [@media(max-width:767px)]:pt-28'}`}
        >
          {children}
        </div>
        <AdHost slot="general_bottom" className="py-24" />
      </div>
      <Footer className="container mx-auto mt-48 flex-shrink-0 px-24" />
    </div>
  );
}
